// 名も無き亡霊「亡霊の波」(shadeRipple) の見た目＝「床をすべる霧と柱」(2026-10-10・ユーザー採用 mist-game「このまま配布版へ」で既定は霧と柱)。
//
// 出所:
//   ユーザー「床の波は完全に仮で作ってもらったままだから、見本を作るところから」。
//   手本＝Hollow Knight Silksong／Last Judge の床をすべる霧と柱 (低い霧が先に横へ走り、あとから柱が順に立つ)。
//     調べ＝docs/research/2026-10-10-floor-fx-references.md の W1。
//   見本の絵＝C:/dev/LUNAIRE-notes/assets/2026-10-10-floor-fx-mihon/floor_fx_shade_wave_v2.png (ユーザー「この方向で作る」)。
//     上＝予告：裾から床に低い霧が這う／中＝当たり：青白い柱が3本、亡霊に近いほど高い／下＝柱が細って消える。
//
// 作り方 (docs/ledger/production.md「FXの作り方」2026-09-28):
//   材料＝見本の絵そのものから切り出した霧の帯1本・柱3本・細った柱3本 (public/sprites/fx/hazard/shade_mist/)。
//     3コマの背景はほぼ同じなので、画素ごとに一番暗いコマを「背景」とみなして引き、床の行は帯の左の床の行ごとの中央値を引いた。
//     色は明るさから すみれ→藤→青白 へ塗り直した (赤・橙・金は無い)。黒背景・加算で描く (覚醒王の大暴れの帯と同じ扱い)。
//     切り出しの手順は C:/dev/LUNAIRE-notes/assets/2026-10-10-shade-wave-game/README.md。
//   動き・時刻・揺れ・消え方はこのファイルが持つ:
//     ① 予告の頭 (0〜520ms)：霧が裾の側 (帯の亡霊に近い端) から外へ走る。通った所は低く盛り上がってから落ち着く (波頭)。
//     ② 予告の後半：柱の根元が近い側から順に灯り、柱の先が少しのぞく。
//     ③ 当たりの直前：柱が亡霊に近い側から順に立ち、当たりの開始で3本とも立ちきる。
//     ④ 当たりの後半：柱が細った形へ移って上へ抜け、霧ごと消える。
//   線の効果 (放射線・効果線) は描かない (memory feedback_fx_no_line_streaks_1010)。
//
// 🔴 見た目だけ。当たりの場所・時間・範囲は変えない (attacks.ts・hits.ts・boss.ts は触らない)。
//    絵は帯 [xStart, xEnd] の中だけに描く (クリップ)。
//
// 開発版の切り替え ?shadeWaveLook=now|old|off で旧の見た目へ戻して見比べる (読むのは下の DEV の箱の中だけ)。指定なし／他の値＝既定の霧と柱。配布版は常に霧と柱。
//   ⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs が配布ビルドの文字列を見張る)。

import { ARENA } from './balance'
import { glowMote, hash01 } from './fx_kit'
import type { ActiveHit } from './types'
import { sw as swc, swapImage } from './purple_swap'

/** URL の値を読む。now / old / off = 旧の見た目 (false)・mist = 霧と柱 (true)・その他 = null (既定の霧と柱)。 */
export function parseShadeMistLook(v: unknown): boolean | null {
  const k = typeof v === 'string' ? v.toLowerCase() : ''
  if (k === 'now' || k === 'old' || k === 'off') return false
  if (k === 'mist') return true
  return null
}
let mistOn = true
/** 「床をすべる霧と柱」を使うか。配布版は常に true (旧の見た目へ戻せるのは DEV の URL だけ)。 */
export function shadeMistLookOn(): boolean { return mistOn }
/** テスト用。配布版では URL を読まないので、ここを呼ぶ物はテストだけ。 */
export function setShadeMistLookForTest(next: boolean): void { mistOn = next }

// ---------------------------------------------------------------- 時刻 (純関数・テストで約束を見る)

/** 霧が帯の亡霊側の端から外の端まで走る時間 (ms)。手本の霧の走り (約0.5秒) に合わせた。 */
export const MIST_RUN_MS = 520
/** 柱が立ち始めるのは当たりの何 ms 前か (近い柱)。遠い柱ほど PILLAR_STAGGER_MS ずつ遅れる。 */
export const PILLAR_LEAD_MS = 190
export const PILLAR_STAGGER_MS = 45
/** 柱1本が立ちきるまでの時間 (ms)。遠い柱が当たりの開始でちょうど立ちきる (190 - 2*45 - 100 = 0)。 */
export const PILLAR_RISE_MS = 100
/** この見た目の時、亡霊が「放つ」絵へ変わるのは当たりの何 ms 前か (柱が立ち始める直前)。 */
export const MIST_CAST_LEAD_MS = 200

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v))

/** 霧の先端のぼかし幅 (帯の幅に対する割合)。先端はこの幅で 0→1 に濃くなる。 */
export const MIST_FRONT_SOFT = 0.08

/**
 * 霧の先端の位置 (0＝亡霊側の端・1＝外の端)。走り出しが速く、端で少し緩む。
 *   ぼかしの分だけ外へ抜ける (MIST_RUN_MS で 1 + MIST_FRONT_SOFT＝外の端まで濃さが満ちる)。
 */
export function mistFrontPos(elapsedMs: number): number {
  const p = clamp01(elapsedMs / MIST_RUN_MS)
  return (1 + MIST_FRONT_SOFT) * (1 - Math.pow(1 - p, 1.6))
}

/** 帯の中の位置 nearFrac (0＝亡霊側の端・1＝外の端) を霧の先端が通った時刻 (ms)。 */
export function mistPassMs(nearFrac: number): number {
  const f = clamp01(nearFrac / (1 + MIST_FRONT_SOFT))
  return MIST_RUN_MS * (1 - Math.pow(1 - f, 1 / 1.6))
}

/** 帯の中の位置 nearFrac に霧がどれだけ満ちたか 0..1 (先端のぼかし込み)。 */
export function mistReach(elapsedMs: number, nearFrac: number): number {
  return clamp01((mistFrontPos(elapsedMs) - nearFrac) / MIST_FRONT_SOFT)
}

/** 柱 k (0＝亡霊に一番近い・2＝一番遠い) の立ち上がり 0..1 (当たりの開始からの ms で見る。負＝予告の中)。 */
export function pillarRise(k: number, fromActiveMs: number): number {
  const start = -PILLAR_LEAD_MS + PILLAR_STAGGER_MS * k
  return clamp01((fromActiveMs - start) / PILLAR_RISE_MS)
}

/** 当たりの中の消え方。thin＝細った柱へ移った割合、gone＝消えた割合 (どちらも 0..1)。 */
export function mistDissolve(activeProg: number): { thin: number; gone: number } {
  return {
    thin: clamp01((activeProg - 0.45) / 0.35),
    gone: clamp01((activeProg - 0.8) / 0.2),
  }
}

// ---------------------------------------------------------------- 材料

type PartName = 'mist_strip' | 'pillar_s' | 'pillar_m' | 'pillar_l' | 'thin_s' | 'thin_m' | 'thin_l'
const PART_NAMES: PartName[] = ['mist_strip', 'pillar_s', 'pillar_m', 'pillar_l', 'thin_s', 'thin_m', 'thin_l']
const parts: Partial<Record<PartName, HTMLImageElement>> = {}
let loadStarted = false
function ensureParts(): boolean {
  if (!loadStarted && typeof Image !== 'undefined') {
    loadStarted = true
    for (const name of PART_NAMES) {
      const img = new Image()
      img.onload = () => { parts[name] = img }
      img.src = `/sprites/fx/hazard/shade_mist/${name}.png`
    }
  }
  return PART_NAMES.every((n) => parts[n])
}

// 見本の絵の座標 (1024 幅の絵の px)。亡霊は右＝帯の亡霊側の端が右。
//   帯の外の端＝x 380、亡霊側の端＝x 722 (霧と柱がつながる裾の手前)。床の線＝y 473。
const MIHON_FAR_X = 380
const MIHON_NEAR_X = 722
const MIHON_FLOOR_Y = 473
// 切り出した部品の左上 (見本の絵の座標)。柱は 0＝亡霊に近い (一番高い)。隣の柱とは 16px 重ね、端をぼかして重なりで足すと 1 になる。
const MIST_SRC = { x: 380, y: 380 }
// 霧の帯は見本で x 392 あたりから始まるので、霧だけ 392〜722 を帯いっぱいに当てる (外の端まで霧が届いて見えるように)。
const MIST_FAR_X = 392
const PILLARS: Array<{ full: PartName; thin: PartName; x: number; y: number }> = [
  { full: 'pillar_l', thin: 'thin_l', x: 577, y: 30 },
  { full: 'pillar_m', thin: 'thin_m', x: 462, y: 120 },
  { full: 'pillar_s', thin: 'thin_s', x: 375, y: 200 },
]

const SPIRIT_RGB = '186,224,242'   // 亡霊の眼と同じ青白 (render.ts SHADE_SPIRIT_RGB と同じ)

/**
 * 亡霊の波を「床をすべる霧と柱」で描く。材料が読み込めていなければ false (呼び手は今の見た目で描く)。
 *   bossX＝亡霊の今の x (帯のどちらの端が亡霊側かを決めるだけ)。
 */
export function drawShadeWaveMist(ctx: CanvasRenderingContext2D, hz: ActiveHit, t: number, bossX: number): boolean {
  if (!ensureParts()) return false
  const w = hz.xEnd - hz.xStart
  if (w <= 2) return true
  const e = hz.elapsedMs
  const T = hz.telegraphMs
  const fromActive = e - T
  const activeProg = fromActive <= 0 ? 0 : clamp01(fromActive / Math.max(1, hz.activeMs))
  const { thin, gone } = mistDissolve(activeProg)
  const live = 1 - gone
  if (live <= 0.005) return true
  const floorY = ARENA.floorY
  const top = floorY - BUF_UP
  // 一度、帯の大きさの下書き用キャンバスへ見本の向き (亡霊が右) で描き、両端をぼかしてから本番へ足す。
  //   ・帯の外へは1画素も出ない (下書きの大きさ＝帯)。
  //   ・短冊の境目を画面の画素にそろえる (加算で重なった所が筋に見えるのを防ぐ)。
  const m = ctx.getTransform()
  const k = Math.max(1, Math.hypot(m.a, m.b))   // ゲームの 1px が画面の何画素か (RENDER_SCALE × カメラ)
  const bw = Math.max(1, Math.floor(w * k))
  const bh = Math.ceil((BUF_UP + BUF_DOWN) * k)
  const b = bufferCtx(bw, bh)
  if (!b) return false
  b.setTransform(1, 0, 0, 1, 0, 0)
  b.globalCompositeOperation = 'source-over'
  b.globalAlpha = 1
  b.clearRect(0, 0, bw, bh)
  b.setTransform(k, 0, 0, k, -hz.xStart * k, -top * k)
  b.globalCompositeOperation = 'lighter'
  const snapX = (x: number): number => hz.xStart + Math.round((x - hz.xStart) * k) / k
  const snapY = (y: number): number => top + Math.round((y - top) * k) / k

  const s = w / (MIHON_NEAR_X - MIHON_FAR_X)   // 見本の px → ゲームの px (帯 180px なら 0.526)
  const toX = (mx: number): number => hz.xStart + (mx - MIHON_FAR_X) * s
  const sMist = w / (MIHON_NEAR_X - MIST_FAR_X)
  const toMistX = (mx: number): number => hz.xStart + (mx - MIST_FAR_X) * sMist
  const nearFracOfX = (gx: number): number => clamp01((hz.xStart + w - gx) / w)   // 0＝亡霊側の端
  const pillarsUp = (pillarRise(0, fromActive) + pillarRise(1, fromActive) + pillarRise(2, fromActive)) / 3

  // ---- ① 霧 (縦の短冊に切って、通った所から盛り上げる)
  const mist = parts.mist_strip!
  // 紫の技を替える比較版 (DEV のみ・src/purple_swap.ts): 弾けない技なので 'unparry' の色へ写した絵で描く。既定はそのまま。
  const mistSrc = swapImage('unparry', mist)
  const late = clamp01((e - T * 0.55) / (T * 0.45))   // 予告の後半ほど霧が濃く高い
  // 柱が立つと、柱の絵の根元の霧が床を覆うので、帯の霧は薄くする (重ねて白く飛ばさない)
  const mistAlphaBase = (0.62 + 0.30 * late) * (1 - 0.5 * pillarsUp) * live
  const SLICE = 5   // 見本の px
  const floorAnchorY = (MIHON_FLOOR_Y - MIST_SRC.y)   // 部品の中の床の線
  // 床の照り (霧の通った所だけ。危ない面を光で読ませる＝2026-07-06 の「見えにくい」への答えを引き継ぐ)
  const washH = 30
  const wash = b.createLinearGradient(0, floorY - washH, 0, floorY + 6)
  wash.addColorStop(0, `rgba(${swc('unparry', SPIRIT_RGB)},0)`)
  wash.addColorStop(0.75, `rgba(${swc('unparry', SPIRIT_RGB)},0.38)`)
  wash.addColorStop(1, `rgba(${swc('unparry', SPIRIT_RGB)},0)`)
  for (let sx = 0; sx < mist.naturalWidth; sx += SLICE) {
    const sw = Math.min(SLICE, mist.naturalWidth - sx)
    const x0 = snapX(toMistX(MIST_SRC.x + sx))
    const x1 = snapX(toMistX(MIST_SRC.x + sx + sw))
    if (x1 <= x0) continue
    const nf = nearFracOfX((x0 + x1) / 2)
    const reach = mistReach(e, nf)
    if (reach <= 0) continue
    const since = e - mistPassMs(nf)   // この短冊を先端が通ってからの ms
    // 波頭: 通った直後に高く盛り上がり (×1.3)、300ms で落ち着く
    const crestP = clamp01(since / 120)
    const settle = clamp01((since - 120) / 240)
    const crest = (0.35 + 0.95 * easeOut(crestP)) * (1 - settle) + 1.0 * settle
    // 落ち着いた後も低くうねる (隣の短冊とずらして、霧が床を転がって見える)
    const roll = 1 + (0.10 + 0.10 * late) * Math.sin(t * 2.6 + sx * 0.045) + 0.06 * Math.sin(t * 4.1 - sx * 0.08)
    const vs = crest * roll * (1 + 0.15 * thin)
    const dh = mist.naturalHeight * s * vs
    const dyTop = floorY - floorAnchorY * s * vs
    b.globalAlpha = clamp01(mistAlphaBase * reach)
    b.drawImage(mistSrc, sx, 0, sw, mist.naturalHeight, x0, dyTop, x1 - x0, dh)
    // 床の照り
    b.globalAlpha = clamp01((0.20 + 0.14 * late) * (1 - 0.6 * pillarsUp) * reach * live)   // 柱が立ったら柱の根元の光に任せる (床に光の線を引かない)
    b.fillStyle = wash
    b.fillRect(x0, floorY - washH, x1 - x0, washH + 6)
  }

  // ---- ② 柱の根元の灯り (予告の後半から近い順に)
  for (let i = 0; i < PILLARS.length; i++) {
    const p = PILLARS[i]
    const img = parts[p.full]!
    const cx = toX(p.x + img.naturalWidth * 0.5)
    const glowStart = T * 0.6 + 90 * i
    const g = clamp01((e - glowStart) / 260)
    if (g <= 0) continue
    const throb = 0.85 + 0.15 * Math.sin(t * 9 + i * 1.3)
    const afterRise = 1 - 0.45 * pillarRise(i, fromActive)   // 柱が立ったら根元の灯りは控えめに
    const r = (22 + 10 * g + 8 * pillarRise(i, fromActive)) * (i === 0 ? 1.15 : i === 1 ? 1.0 : 0.85)
    b.save()
    b.translate(cx, floorY - 2)
    b.scale(1, 0.32)
    const rg = b.createRadialGradient(0, 0, 0, 0, 0, r)
    rg.addColorStop(0, `rgba(${swc('unparry', SPIRIT_RGB)},${(0.5 * g * throb * afterRise * live).toFixed(3)})`)
    rg.addColorStop(0.5, `rgba(${swc('unparry', '172,162,230')},${(0.22 * g * throb * afterRise * live).toFixed(3)})`)
    rg.addColorStop(1, `rgba(${swc('unparry', '120,96,210')},0)`)
    b.fillStyle = rg
    b.globalAlpha = 1
    b.beginPath()
    b.arc(0, 0, r, 0, Math.PI * 2)
    b.fill()
    b.restore()
  }

  // ---- ③ 柱 (先がのぞく → 近い順に立つ → 細って上へ抜ける)
  for (let i = 0; i < PILLARS.length; i++) {
    const p = PILLARS[i]
    const rise = pillarRise(i, fromActive)
    // 立つ前の「先のぞき」: 当たりの 520ms 前から、近い順に根元の房だけ (高さ 0.2 まで)
    const peek = clamp01((e - (T - 520 + 80 * i)) / 220) * 0.2
    const h = Math.max(peek, rise <= 0 ? 0 : 0.2 + 0.8 * easeOutBack(rise))
    if (h <= 0.01) continue
    const widen = rise > 0 ? 0.72 + 0.28 * easeOut(rise) : 0.8
    const flash = rise > 0 && rise < 1 ? 0.2 * Math.sin(Math.PI * rise) : 0
    const aFull = (rise > 0 ? 0.88 : 0.65) * (1 - thin) * live + flash
    const aThin = 0.92 * thin * live
    const lift = 14 * thin + 18 * gone   // 細った柱は上へ抜けていく
    const anchor = MIHON_FLOOR_Y - p.y
    drawSwayingPillar(b, parts[p.full]!, toX(p.x), floorY, anchor, s, h, widen, aFull, t, i, 0, snapY)
    if (aThin > 0.01) {
      drawSwayingPillar(b, parts[p.thin]!, toX(p.x), floorY, anchor, s, h * (1 + 0.08 * thin), 1 - 0.12 * thin, aThin, t, i, lift, snapY)
    }
  }

  // ---- ④ 柱の先から抜ける青白い粒 (当たりの中だけ・少し)
  if (fromActive > 0) {
    for (let i = 0; i < 9; i++) {
      const p = PILLARS[i % 3]
      const img = parts[p.full]!
      const cx = toX(p.x + img.naturalWidth * (0.3 + 0.4 * hash01(i * 7.1)))
      const topY = floorY - (MIHON_FLOOR_Y - p.y) * s * 0.75
      const q = ((fromActive / 1000) * (0.9 + 0.5 * hash01(i * 3.3)) + hash01(i * 5.7)) % 1
      glowMote(b, cx + Math.sin(q * 5 + i) * 6, topY + 30 - q * 70, 2.2 + 1.6 * hash01(i * 9.1),
        `rgba(${swc('unparry', SPIRIT_RGB)},1)`, Math.sin(Math.PI * q) * 0.7 * live)
    }
  }

  // ---- 両端をぼかす (外の端 8px・亡霊側の端 16px＝裾へ溶かす)。帯の中だけで薄めるので、外へははみ出さない。
  b.setTransform(1, 0, 0, 1, 0, 0)
  b.globalAlpha = 1
  b.globalCompositeOperation = 'destination-in'
  const edge = b.createLinearGradient(0, 0, bw, 0)
  edge.addColorStop(0, 'rgba(0,0,0,0)')
  edge.addColorStop(Math.min(0.45, (8 * k) / bw), 'rgba(0,0,0,1)')
  edge.addColorStop(Math.max(0.55, 1 - (16 * k) / bw), 'rgba(0,0,0,1)')
  edge.addColorStop(1, 'rgba(0,0,0,0)')
  b.fillStyle = edge
  b.fillRect(0, 0, bw, bh)
  b.globalCompositeOperation = 'source-over'

  // ---- 本番へ足す (亡霊が左なら帯の中心で左右を返す)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = 1
  if (bossX < (hz.xStart + hz.xEnd) / 2) { ctx.translate(hz.xStart + hz.xEnd, 0); ctx.scale(-1, 1) }
  ctx.drawImage(bufCanvas!, 0, 0, bw, bh, hz.xStart, top, bw / k, bh / k)
  ctx.restore()
  return true
}

// 下書き用キャンバス (使い回す)。床から上へ BUF_UP・下へ BUF_DOWN (ゲームの px)。
const BUF_UP = 400
const BUF_DOWN = 40
let bufCanvas: HTMLCanvasElement | null = null
let bufCtx: CanvasRenderingContext2D | null = null
function bufferCtx(wDev: number, hDev: number): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null
  if (!bufCanvas) {
    bufCanvas = document.createElement('canvas')
    bufCtx = bufCanvas.getContext('2d')
  }
  if (bufCanvas.width < wDev || bufCanvas.height < hDev) {
    bufCanvas.width = Math.max(bufCanvas.width, wDev)
    bufCanvas.height = Math.max(bufCanvas.height, hDev)
  }
  return bufCtx
}

/**
 * 柱の部品を横の短冊に切り、上ほど大きく揺らして描く (炎のように見えるのは揺れ・形は見本の絵)。
 *   短冊の上下の境目は画面の画素にそろえる (snapY)。隙間も重なりも作らない。
 */
function drawSwayingPillar(
  b: CanvasRenderingContext2D, img: HTMLImageElement, leftX: number, floorY: number, floorAnchor: number,
  s: number, h: number, widen: number, alpha: number, t: number, k: number, lift: number,
  snapY: (y: number) => number,
): void {
  if (alpha <= 0.01 || h <= 0.01) return
  const W = img.naturalWidth
  const H = img.naturalHeight
  const dw = W * s * widen
  const cx = leftX + (W * s) / 2
  const ROW = 6
  const yAt = (sy: number): number => {
    const above = floorAnchor - sy   // 床の線より上の高さ (見本の px)。負＝床の照り返し
    return floorY - above * s * h - lift * clamp01(above / Math.max(1, floorAnchor))
  }
  const src = swapImage('unparry', img)   // 比較版 (DEV のみ)。既定は img そのもの
  b.globalAlpha = clamp01(alpha)
  for (let sy = 0; sy < H; sy += ROW) {
    const rowH = Math.min(ROW, H - sy)
    const y0 = snapY(yAt(sy))
    const y1 = snapY(yAt(sy + rowH))
    if (y1 <= y0) continue
    const hf = clamp01((floorAnchor - sy - rowH / 2) / Math.max(1, floorAnchor))   // 0＝根元・1＝先
    const sway = (1.5 + 5.5 * Math.pow(hf, 1.3)) * Math.sin(t * 6.2 + hf * 4.2 + k * 1.9)
      + 1.8 * Math.pow(hf, 2) * Math.sin(t * 11.3 - hf * 7 + k)
    b.drawImage(src, 0, sy, W, rowH, cx - dw / 2 + sway, y0, dw, y1 - y0)
  }
}

function easeOut(p: number): number { return 1 - Math.pow(1 - clamp01(p), 3) }
function easeOutBack(p: number): number {
  const c = 1.4
  const x = clamp01(p) - 1
  return 1 + (c + 1) * x * x * x + c * x * x
}

// URL の読み取りは最後 (上の let が初期化された後)。配布版はこの箱ごと消える = 常に霧と柱。
if (import.meta.env.DEV && typeof window !== 'undefined') {
  mistOn = parseShadeMistLook(new URLSearchParams(window.location.search).get('shadeWaveLook')) ?? true
}
